import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import logo from './logo.svg';
import './App.css';
import ProductList from './components/ProductList';
import Cart from './components/Cart';
import Checkout from './components/Checkout';
import NavBar from './components/NavBar';
import Hero from './components/Hero';
import ProductPage from './components/ProductPage';
import AdminPage from './components/AdminPage';
import Footer from './components/Footer';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsConditions from './components/TermsConditions';
import ReturnPolicy from './components/ReturnPolicy';
import ShippingPolicy from './components/ShippingPolicy';
import LockedPage from './components/LockedPage';
import PortfolioPage from './components/PortfolioPage';
import FabricSourcesPage from './components/FabricSourcesPage';
import config from './config';
import './assets/fonts/lucidagrande.ttf';

const API_BASE = config.API_BASE;

function MainPage({ cart, handleAddToCart, handleRemoveFromCart, handleChangeQuantity }) {
  return (
    <>
      <Hero />
      <ProductList onAddToCart={handleAddToCart} />
    </>
  );
}

function App() {
  const [cart, setCart] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cart));
  }, [cart]);

  function handleAddToCart(product) {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id && item.selectedSize === product.selectedSize);
      if (existing) {
        return prev.map(item =>
          item.id === product.id && item.selectedSize === product.selectedSize
            ? { ...item, quantity: (item.quantity || 1) + 1 }
            : item
        );
      }
      return [...prev, { ...product, quantity: 1 }];
    });
  }

  function handleRemoveFromCart(id) {
    setCart(prev => prev.filter(item => item.id !== id));
  }

  function handleChangeQuantity(id, quantity) {
    setCart(prev =>
      prev.map(item =>
        item.id === id ? { ...item, quantity: Math.max(1, quantity) } : item
      )
    );
  }

  function handleClearCart() {
    setCart([]);
    localStorage.removeItem('cart');
  }

  function AppWithNav() {
    const navigate = useNavigate();
    const location = useLocation();
    const [isLocked, setIsLocked] = useState(false);
    const [checkingLocked, setCheckingLocked] = useState(true);
    const [bypassActive, setBypassActive] = useState(false);
    
    useEffect(() => {
      const checkLockedState = async () => {
        try {
          if (location.pathname === '/admin' || location.pathname.startsWith('/portfolio') || location.pathname === '/fabrics') {
            setIsLocked(false);
            setCheckingLocked(false);
            return;
          }

          const response = await fetch(`${API_BASE}/api/locked-state`, { credentials: 'include' });
          const data = await response.json();

          if (data.success) {
            setBypassActive(!!data.bypassActive);
            setIsLocked(data.isLocked && !data.bypassActive);
          }
        } catch (error) {
          console.error('Error checking locked state:', error);
          setIsLocked(false);
        } finally {
          setCheckingLocked(false);
        }
      };

      checkLockedState();
    }, [location.pathname]);
    
    // Canonical (non-www to avoid redirect) and per-route title + meta description for crawling
    const BASE = 'https://bokehclo.com';
    const path = location.pathname === '/' ? '' : location.pathname;
    const canonicalUrl = `${BASE}${path}`;
    const seoByPath = {
      '/': { title: 'Bokeh – Unique Clothing, Jewelry & Custom Cut-and-Sew', desc: 'Bokeh – Unique & archive clothing, jewelry, and custom cut-and-sew. Shop one-of-a-kind pieces, handcrafted fashion, and designer apparel. Custom commissions available.' },
      '/privacy-policy': { title: 'Privacy Policy – Bokeh', desc: 'Privacy policy for Bokeh. How we use your data when you shop unique clothing, jewelry, and custom commissions.' },
      '/terms-conditions': { title: 'Terms & Conditions – Bokeh', desc: 'Terms and conditions for Bokeh. Shopping unique clothing, jewelry, and custom cut-and-sew.' },
      '/return-policy': { title: 'Return Policy – Bokeh', desc: 'Return and refund policy for Bokeh. Unique clothing, jewelry, and custom commissions.' },
      '/shipping-policy': { title: 'Shipping Policy – Bokeh', desc: 'Shipping information for Bokeh. Delivery for unique clothing, jewelry, and custom orders.' },
    };
    useEffect(() => {
      const existingCanonical = document.querySelector('link[rel="canonical"]');
      if (existingCanonical) {
        existingCanonical.setAttribute('href', canonicalUrl);
      } else {
        const link = document.createElement('link');
        link.rel = 'canonical';
        link.href = canonicalUrl;
        document.head.appendChild(link);
      }
    }, [location.pathname]);
    useEffect(() => {
      const metaDesc = document.querySelector('meta[name="description"]');
      if (location.pathname.startsWith('/portfolio')) {
        document.title = 'Portfolio – Bokeh';
        if (metaDesc) {
          metaDesc.setAttribute(
            'content',
            'Portfolio — Cargo template (embedded mirror). Graphic design, 3D, and creative work.'
          );
        }
        return;
      }
      if (location.pathname === '/fabrics') {
        document.title = 'Fabric Sources – Bokeh';
        if (metaDesc) {
          metaDesc.setAttribute('content', 'Unlisted fabric buying links — external supplier sources.');
        }
        return;
      }
      const seo = seoByPath[location.pathname];
      if (seo) {
        document.title = seo.title;
        if (metaDesc) metaDesc.setAttribute('content', seo.desc);
      }
    }, [location.pathname]);
    
    // Show locked page if enabled (except for admin and portfolio)
    if (
      !checkingLocked &&
      isLocked &&
      location.pathname !== '/admin' &&
      !location.pathname.startsWith('/portfolio') &&
      location.pathname !== '/fabrics'
    ) {
      return <LockedPage />;
    }
    
    const hideShopChrome = location.pathname.startsWith('/portfolio') || location.pathname === '/admin' || location.pathname === '/fabrics';
    const appModifier = location.pathname === '/admin'
      ? ' App--admin'
      : location.pathname.startsWith('/portfolio')
        ? ' App--portfolio'
        : location.pathname === '/fabrics'
          ? ' App--fabrics'
          : '';

    return (
      <div className={`App${appModifier}`}>
        {!hideShopChrome && (
          <NavBar cartCount={cart.reduce((sum, item) => sum + item.quantity, 0)} onCartClick={() => navigate('/checkout')} />
        )}
        <Routes>
          <Route path="/" element={
            <MainPage
              cart={cart}
              handleAddToCart={handleAddToCart}
              handleRemoveFromCart={handleRemoveFromCart}
              handleChangeQuantity={handleChangeQuantity}
            />
          } />
          <Route path="/market" element={<Navigate to="/" replace />} />
          <Route path="/product/:slug" element={<ProductPage onAddToCart={handleAddToCart} />} />
          <Route path="/checkout" element={<Checkout cart={cart} onChangeQuantity={handleChangeQuantity} onRemoveFromCart={handleRemoveFromCart} onClearCart={handleClearCart} />} />
          <Route path="/commissions" element={<Navigate to="/" replace />} />
          <Route path="/commission/:slug" element={<Navigate to="/" replace />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-conditions" element={<TermsConditions />} />
          <Route path="/return-policy" element={<ReturnPolicy />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/portfolio/*" element={<PortfolioPage />} />
          <Route path="/fabrics" element={<FabricSourcesPage />} />
        </Routes>
        {!hideShopChrome && <Footer />}
      </div>
    );
  }

  return (
    <Router>
        <AppWithNav />
    </Router>
  );
}

export default App;
